import React, { ReactNode } from 'react';
import { CardContext, ImageAspectRatio, ModuleType } from 'types/Common';
import {
    isBroadcast,
    isMovieCard,
    isNowAndNext,
    isPlaceholder,
    isProgramMovie,
    isRecording,
    isSeries,
    isTvSeriesEpisode,
    isTVChannel,
    isVODEpisodeCard,
    isContinueWatching,
} from 'utils/fnTypeGuards';
import { BroadcastCardPresenter } from 'components/Presenters/StripeCollection/BroadcastCardPresenter';
import { cardTrackingData } from 'utils/fnTracking';
import { RecordingCardPresenter } from 'components/Presenters/StripeCollection/RecordingCardPresenter';
import { NowAndNextCardPresenter } from 'components/Presenters/StripeCollection/NowAndNextCardPresenter';
import { MovieCardPresenter } from 'components/Presenters/StripeCollection/MovieCardPresenter';
import { SeriesCardPresenter } from 'components/Presenters/StripeCollection/SeriesCardPresenter';
import { VODEpisodeCardPresenter } from 'components/Presenters/StripeCollection/VODEpisodeCardPresenter';
import { TVEpisodeCardPresenter } from 'components/Presenters/StripeCollection/TVEpisodeCardPresenter';
import { BasePresenterSelector } from 'components/PresenterSelector/BasePresenterSelector';
import { PlaceholderCardPresenter } from 'components/Presenters/StripeCollection/PlaceholderCardPresenter';
import { ContinueWatchingCardPresenter } from 'components/Presenters/StripeCollection/ContinueWatchingCardPresenter';
import { CardChannel } from 'components/Cards/CardChannel';

export class StripePresenterSelector extends BasePresenterSelector {
    private stripeType: ModuleType;

    private title: string;

    private onCardClick: (event: React.MouseEvent<any>, data: any) => void;

    private screenName: string;

    private position: number;

    protected variant: string;

    protected aspectRatio: ImageAspectRatio;

    static readonly create = (): StripePresenterSelector => new StripePresenterSelector();

    setType = (stripeType: ModuleType): StripePresenterSelector => {
        this.stripeType = stripeType;
        return this;
    };

    setTitle = (title: string): StripePresenterSelector => {
        this.title = title;
        return this;
    };

    setScreenName = (screenName: string): StripePresenterSelector => {
        this.screenName = screenName;
        return this;
    };

    setPosition = (position: number): StripePresenterSelector => {
        this.position = position;
        return this;
    };

    setCardClickListener = (clickListener: (event: React.MouseEvent<any>, data: any) => void): StripePresenterSelector => {
        this.onCardClick = clickListener;
        return this;
    };

    setVariant(variant: string) {
        this.variant = variant;
        return this;
    }

    setAspectRatio(aspectRatio: ImageAspectRatio) {
        this.aspectRatio = aspectRatio;
        return this;
    }

    get = (): ReactNode[] => {
        if (!this.assets) return null;
        const cards = [];

        for (let i = 0; i < this.assets.length; i += 1) {
            const asset = this.assets[i];

            const presenterKey = `${asset.id}-${i}`;

            if (isPlaceholder(asset)) {
                cards.push(<PlaceholderCardPresenter key={presenterKey} asset={asset} cardContext={CardContext.STRIPE} />);
            }

            if (isTVChannel(asset)) {
                cards.push(<CardChannel key={presenterKey} asset={asset} />);
            }

            if (isBroadcast(asset)) {
                const trackingType = isProgramMovie(asset) ? 'program_movie_card' : 'program_card';
                cards.push(
                    <BroadcastCardPresenter
                        key={presenterKey}
                        asset={asset}
                        type={this.stripeType}
                        onClick={this.onCardClick}
                        aspectRatio={this.aspectRatio}
                        cardContext={CardContext.STRIPE}
                        variant={this.variant}
                        trackingData={cardTrackingData(trackingType, this.screenName, i, this.title, asset.id, asset.title, this.position)}
                    />
                );
            }

            if (isRecording(asset)) {
                cards.push(
                    <RecordingCardPresenter
                        key={presenterKey}
                        asset={asset}
                        type={this.stripeType}
                        cardContext={CardContext.STRIPE}
                        trackingData={cardTrackingData(
                            'recording_card',
                            this.screenName,
                            i,
                            this.title,
                            asset.id,
                            asset.title,
                            this.position
                        )}
                    />
                );
            }

            if (isNowAndNext(asset)) {
                cards.push(
                    <NowAndNextCardPresenter
                        key={presenterKey}
                        asset={asset}
                        cardContext={CardContext.STRIPE}
                        onClick={this.onCardClick}
                        trackingData={cardTrackingData(
                            'now_next_card',
                            this.screenName,
                            i,
                            this.title,
                            asset.id,
                            asset.title,
                            this.position
                        )}
                    />
                );
            }

            if (isContinueWatching(asset)) {
                cards.push(
                    <ContinueWatchingCardPresenter
                        key={presenterKey}
                        asset={asset}
                        cardContext={CardContext.STRIPE}
                        onClick={this.onCardClick}
                        trackingData={cardTrackingData(
                            'continue_watching_card',
                            this.screenName,
                            i,
                            this.title,
                            asset.id,
                            asset.title,
                            this.position
                        )}
                    />
                );
            }

            if (isMovieCard(asset)) {
                cards.push(
                    <MovieCardPresenter
                        key={presenterKey}
                        asset={asset}
                        type={this.stripeType}
                        aspectRatio={this.aspectRatio}
                        cardContext={CardContext.STRIPE}
                        trackingData={cardTrackingData('movie_card', this.screenName, i, this.title, asset.id, asset.title, this.position)}
                    />
                );
            }

            if (isSeries(asset)) {
                cards.push(
                    <SeriesCardPresenter
                        key={presenterKey}
                        asset={asset}
                        type={this.stripeType}
                        aspectRatio={this.aspectRatio}
                        cardContext={CardContext.STRIPE}
                        trackingData={cardTrackingData('series_card', this.screenName, i, this.title, asset.id, asset.title, this.position)}
                    />
                );
            }

            if (isVODEpisodeCard(asset)) {
                cards.push(
                    <VODEpisodeCardPresenter
                        key={presenterKey}
                        asset={asset}
                        cardContext={CardContext.STRIPE}
                        showDescription={this.showEpisodeDescription}
                        hideGenre={this.hideGenre}
                        onClick={this.onCardClick}
                        trackingData={cardTrackingData(
                            'vod_episode_card',
                            this.screenName,
                            i,
                            this.title,
                            asset.id,
                            asset.title,
                            this.position
                        )}
                    />
                );
            }

            if (isTvSeriesEpisode(asset)) {
                cards.push(
                    <TVEpisodeCardPresenter
                        key={presenterKey}
                        asset={asset}
                        cardContext={CardContext.STRIPE}
                        hideChannelLogo={this.hideChannelLogo}
                        showDescription={this.showEpisodeDescription}
                        hideGenre={this.hideGenre}
                        onClick={this.onCardClick}
                        trackingData={cardTrackingData(
                            'tv_episode_card',
                            this.screenName,
                            i,
                            this.title,
                            asset.id,
                            asset.title,
                            this.position
                        )}
                    />
                );
            }
        }

        return cards;
    };
}
